import { Observable, map, mergeMap } from 'rxjs';

import { Injectable, computed, inject } from '@angular/core';

import { Study, StudySubcategory, StudyUnit } from '@mitsubishi/shared/data-access';

import { Store } from '@ngxs/store';

import { StudyUtils } from '../../utils/study.utils';
import { ProjectSelectors } from '../project/project.selectors';
import { StudyReadSource } from './study-read-source';
import { StudySelectors } from './study.selectors';

/**
 * The legacy backing of `StudyReadSource`: the studies embedded in the project GET, read through the NGXS
 * selectors exactly as `StudyFacade` did before P1. Chosen while the new study page is off; dies with the
 * legacy page (PLAN.md P4).
 */
@Injectable({ providedIn: 'root' })
export class NgxsStudyReadSource implements StudyReadSource {
  readonly #store = inject(Store);

  readonly #studies = this.#store.selectSignal(ProjectSelectors.projectStudies);
  /** Permanent studies only, as `/studies` returns them on the other backing (D5). */
  readonly studySummaries = computed(() =>
    this.#studies()
      .filter(study => !study.isTemporary)
      .map(study => StudyUtils.toStudySummary(study))
  );

  readonly activeStudy$ = this.#store.select(StudySelectors.getActiveStudy);
  readonly isActiveStudyTemporary = this.#store.selectSignal(StudySelectors.isActiveStudyTemporary);
  readonly activeStudyCurrencySymbol = this.#store.selectSignal(StudySelectors.activeStudyCurrencySymbol);
  readonly currentStudyOrganizationId = this.#store.selectSignal(StudySelectors.currentStudyOrganizationId);
  readonly activeUnit$: Observable<StudyUnit | undefined> = this.#store.select(StudySelectors.getActiveUnit);

  readonly #units$ = this.activeStudy$.pipe(
    map(study => Object.values(study?.subcategories ?? {}).flatMap(subcategory => Object.values(subcategory.units)))
  );
  readonly selectedUnit$: Observable<StudyUnit | undefined> = this.#store
    .select(StudySelectors.getRouteUnitId)
    .pipe(mergeMap(unitId => this.#units$.pipe(map(units => units.find(unit => unit.uuid === unitId)))));

  readonly canUpdateProjectCertificationOrUnitMeasures$ = this.#store.select(
    ProjectSelectors.canUpdateProjectCertificationOrUnitMeasures
  );

  get activeStudy(): Study | undefined {
    return this.#store.selectSnapshot(StudySelectors.getActiveStudy);
  }

  get isActiveStudyCertified(): boolean {
    return this.#store.selectSnapshot(StudySelectors.isActiveStudyCertified);
  }

  getStudyById(studyId: string): Study | undefined {
    return this.#store.selectSnapshot(StudySelectors.getStudyById(studyId));
  }

  studyById$(studyId: string): Observable<Study | undefined> {
    return this.#store.select(StudySelectors.getStudyById(studyId));
  }

  getSubcategoryByUnitId(unitId: string): StudySubcategory | undefined {
    return this.#store.selectSnapshot(StudySelectors.getSubcategoryByUnitId(unitId));
  }

  getSubcategoryIdByUnitId(unitId: string): string | undefined {
    return this.getSubcategoryByUnitId(unitId)?.uuid;
  }

  getUnit(unitId: string | undefined): StudyUnit | undefined {
    return this.#store.selectSnapshot(StudySelectors.getUnit(unitId));
  }

  getExternalUnit(unitId: string | undefined, workingConditionId: string): StudyUnit | undefined {
    return this.#store.selectSnapshot(StudySelectors.getExternalUnit(unitId, workingConditionId));
  }

  externalUnitById$(unitId: string, workingConditionId: string): Observable<StudyUnit | undefined> {
    return this.#store.select(StudySelectors.getExternalUnit(unitId, workingConditionId));
  }
}
